import { useLayoutEffect, useState } from "react";
import { seasonFor } from "../../../shared/kitchen/season";
import { useHousehold } from "../../app/useHousehold";
import { useNow } from "../../ui/time";
import { remPx, useBoxSize } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./kitchen.css";

interface SeasonConfig {
  showStored: boolean;
}

/** What's growing in Colorado this month: fresh, last call, coming soon, and from storage. */
function InSeason({ config, size, fullscreen, openFullscreen }: WidgetProps<SeasonConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const month = Number(new Intl.DateTimeFormat("en-US", { timeZone, month: "numeric" }).format(now));
  const monthName = new Intl.DateTimeFormat("en-US", { timeZone, month: "long" }).format(now);
  const s = seasonFor(month);
  const headline = s.fresh.find((f) => f.note);
  const roomy = fullscreen || size.height > remPx() * 18;
  const [ref, box] = useBoxSize<HTMLUListElement>();
  const [hidden, setHidden] = useState(0);
  // Show only chips that fit whole; the rest are counted underneath.
  useLayoutEffect(() => {
    const ul = ref.current;
    if (!ul) return;
    let n = 0;
    for (const li of ul.children as HTMLCollectionOf<HTMLElement>) {
      li.style.visibility = "";
      if (li.offsetTop + li.offsetHeight > ul.clientHeight + 1) {
        li.style.visibility = "hidden";
        n++;
      }
    }
    setHidden(n);
  }, [box.width, box.height, month, ref]);

  return (
    <div className="season">
      <WidgetHeader title={`In season · ${monthName}`} meta={size.width > 420 ? "Colorado-grown" : undefined} onTitle={fullscreen ? undefined : openFullscreen} />
      {headline && <p className="season-headline">{headline.emoji} {headline.note}</p>}
      <ul className="season-chips" ref={ref}>
        {s.fresh.map((f) => (
          <li key={f.name} className={f.lastCall ? "is-last" : f.justIn ? "is-new" : ""}>
            <span aria-hidden="true">{f.emoji}</span>
            {f.name}
            {f.justIn && <em>New</em>}
            {f.lastCall && <em>Last call</em>}
          </li>
        ))}
      </ul>
      {hidden > 0 && <p className="season-more">and {hidden} more</p>}
      {roomy && s.comingSoon.length > 0 && (
        <p className="season-more">
          <b>Coming soon</b> {s.comingSoon.map((f) => f.name).join(", ")}
        </p>
      )}
      {roomy && config.showStored && s.stored.length > 0 && (
        <p className="season-more">
          <b>From storage & greenhouses</b> {s.stored.map((f) => f.name).join(", ")}
        </p>
      )}
    </div>
  );
}

export const seasonWidget: WidgetDefinition<SeasonConfig> = {
  type: "in-season",
  version: 1,
  name: "What’s in season",
  description: "Colorado-grown fruit and vegetables this month, what’s just arrived, last call, and what’s coming next.",
  icon: "leaf",
  category: "household",
  defaultSize: { w: 4, h: 5 },
  minSize: { w: 3, h: 3 },
  stackHeight: 340,
  defaults: { showStored: true },
  settings: [{ key: "showStored", label: "Include storage and greenhouse crops", type: "toggle" }],
  supportsFullscreen: true,
  Component: InSeason,
};
